// Responsive glance stats row — shows as many weekly highlights as fit, drops lowest priority first.
import { Box, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { StatBlock } from '@/components/ui/SharedComponents';
import { formatHours } from '@/components/ui/formatHours';
import { CAPACITY_WARNING_COLOR } from '@/utils/colorUtils';
import { useT } from '@/i18n/useT';
import { selectGlanceStatIds } from '@/features/dashboard/utils/dashboardGlanceStatsUtils';

const formatSignedDeltaHours = (delta) =>
  `${delta >= 0 ? '+' : ''}${formatHours(Math.abs(delta))}`;

const statBlockSx = { mb: 0, p: 2, height: '100%', boxSizing: 'border-box' };

export function DashboardGlanceStats({
  breakpoint,
  totalHours,
  totalTasks,
  activeDays,
  delta,
  bestDay,
  biggestTask,
}) {
  const t = useT();

  const availability = {
    totalHours: true,
    totalTasks: totalTasks > 0,
    activeDays: activeDays > 0,
    bestDay: Boolean(bestDay),
    delta: delta !== null && delta !== undefined && delta !== 0,
    biggestTask: Boolean(biggestTask),
  };

  const visibleIds = selectGlanceStatIds(availability, breakpoint);

  const renderStat = (id) => {
    switch (id) {
      case 'totalHours':
        return (
          <StatBlock
            dense
            value={formatHours(totalHours)}
            label={t('dashboard.totalHours', 'voltooid deze week')}
            sx={statBlockSx}
          />
        );
      case 'totalTasks':
        return (
          <StatBlock
            dense
            value={totalTasks}
            label={t('dashboard.totalTasks', 'taken voltooid')}
            sx={statBlockSx}
          />
        );
      case 'activeDays':
        return (
          <StatBlock
            dense
            value={activeDays}
            label={t('dashboard.activeDays', 'actieve dagen')}
            sx={statBlockSx}
          />
        );
      case 'bestDay':
        return (
          <StatBlock
            dense
            value={bestDay}
            label={t('dashboard.bestDay', 'beste dag')}
            sx={statBlockSx}
          />
        );
      case 'delta':
        return (
          <StatBlock
            dense
            value={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                {delta >= 0 ? (
                  <TrendingUpIcon
                    sx={{ fontSize: 16, color: 'success.main' }}
                  />
                ) : (
                  <TrendingDownIcon
                    sx={{ fontSize: 16, color: CAPACITY_WARNING_COLOR }}
                  />
                )}
                <Box component="span" sx={{ fontSize: 13, fontWeight: 700 }}>
                  {formatSignedDeltaHours(delta)}
                </Box>
              </Box>
            }
            label={t('dashboard.delta', 't.o.v. vorige week')}
            sx={statBlockSx}
          />
        );
      case 'biggestTask':
        if (!biggestTask) return null;
        return (
          <StatBlock
            dense
            value={
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {biggestTask.title}
              </Typography>
            }
            label={t('dashboard.biggestTaskShort', 'grootste taak')}
            sx={statBlockSx}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      data-testid="dashboard-glance-stats"
      inert
      sx={{
        display: 'flex',
        gap: 1.5,
        width: '100%',
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }}
    >
      {visibleIds.map((id) => (
        <Box key={id} sx={{ flex: '1 1 0', minWidth: 0 }}>
          {renderStat(id)}
        </Box>
      ))}
    </Box>
  );
}

export default DashboardGlanceStats;
